import * as z from 'zod';

import { zodDateOrString } from '../../utils';

/**
 * #### Ginnie Mae Payment Default Status (PDS) Report
 * Loan-level default reporting submitted monthly to Ginnie Mae per Appendix VI-22.
 * Reports delinquent loans, loans in bankruptcy/forbearance, and loans where the issuer
 * is pursuing an alternative to foreclosure.
 *
 * The PDS file is a fixed-width ASCII format with H (Header), D (Detail), and T (Trailer) records.
 * D records are variable-length (52–261 chars) depending on the number of action code/date pairs.
 */

/**
 * ##### PDS Action Codes (Appendix VI-22 §11)
 * Action codes are EVENT codes, not status codes. They are reported in the period the event occurs.
 *
 * The following codes repeat monthly while the underlying condition persists:
 * - 006 (Forbearance), 009 (Special Forbearance), 091 (Trial Modification),
 *   032 (Military Indulgence) — reuse the SAME Action Code Date each month
 * - 042 (Delinquent, No Action) — repeats monthly when no other action applies,
 *   uses the current reporting cutoff date
 */
export const GinnieMaePDSActionCodes = z.enum([
  // General Delinquency
  '042', // Delinquent, No Action
  '078', // Borrower Program Assistance Received (USDA only)
  '034', // Natural Disaster Assistance Warranted
  '089', // Program Specific Moratorium
  '080', // Breach Letter Sent
  '083', // Solicitation Letter Sent

  // Delinquency Workouts
  '026', // Refinance Started
  '084', // Complete Borrower Response Package Received
  '088', // Streamlined Borrower Financials Received
  '085', // Ineligible for Loss Mitigation
  '086', // Ineligible for Loss Mitigation Due to No Response
  '087', // Workout Failure
  '011', // Promise to Pay
  '009', // Special Forbearance (repeat monthly)
  '006', // Forbearance (repeat monthly)
  '012', // Repayment Plan
  '010', // Partial Claim Started
  '051', // Payment Supplement (FHA only)
  '028', // Modification Started
  '091', // Trial Modification or Payment Plan Started (repeat monthly)
  '036', // RD-SLS Loan Modification Started (USDA only)
  '039', // Insuring Agency-Specific Trial Payment Plan
  '041', // RD-SLS Modification with Mortgage Recovery Advance (USDA only)
  '082', // Prequalified for 601 (FHA only)
  '015', // Pre-foreclosure / Short Sale Plan Approved
  '017', // Pre-foreclosure / Short Sale Completed
  '044', // Deed-in-Lieu Started
  '038', // Recovery Modification Started without a Partial Claim
  '061', // Recovery Modification Started with a Partial Claim
  '053', // Combination Partial Claim / Modification Started (Non FHA-HAMP)
  '055', // Advance Modification Started
  '037', // FHA-HAMP Standalone Modification Started (FHA only)
  '136', // FHA-HAMP Standalone Partial Claim Started (FHA only)
  '141', // FHA-HAMP Modification/Partial Claim Started (FHA only)

  // Reinstatement
  '020', // Reinstated by Borrower Without Loss Mitigation
  '021', // Reinstated by Assumptor
  '098', // Reinstated after Loss Mitigation Intervention

  // Bankruptcy
  '059', // Chapter 12 Bankruptcy
  '065', // Chapter 7 Bankruptcy
  '066', // Chapter 11 Bankruptcy
  '067', // Chapter 13 Bankruptcy
  '069', // Bankruptcy Plan Confirmed
  '076', // Bankruptcy Court Clearance Obtained

  // Foreclosure
  '043', // Foreclosure Referral
  '071', // Foreclosure Sale Scheduled
  '068', // First Legal Action to Commence Foreclosure
  '025', // Foreclosure Sale Held
  '027', // 3rd Party Foreclosure Sale Held
  '023', // Eviction Started
  '024', // Government Seizure
  '031', // Probate
  '032', // Military Indulgence (repeat monthly)
  '033', // Contested or Litigated Foreclosure
  '095', // State Mandated Delay and/or Mediation
  '096', // Federal Law Mandated Delay and/or Mediation
  '122', // Post-Foreclosure Initial Exclusive Period (FHA only)
  '124', // Post-Foreclosure Extended Sale Period (FHA only)

  // Termination
  '029', // Charge-off
  '030', // Third-Party Sale
  '049', // Assignment
  '013', // Paid in Full
  '022', // Pool Transfer
  '046', // Property Conveyed to Insuring Agency
  '047', // Deed-in-Lieu Completed & Property Conveyed
  '048', // Claim without Conveyance of Title
  '073', // Property Redeemed

  // Other
  '177', // Partial Release of a Security (VA only)
]);
export type GinnieMaePDSActionCode = z.infer<typeof GinnieMaePDSActionCodes>;

/** Agency-restricted action codes — valid only for the listed insuring agency */
export const FHA_ONLY_PDS_ACTION_CODES: ReadonlySet<GinnieMaePDSActionCode> = new Set([
  '037',
  '051',
  '082',
  '122',
  '124',
  '136',
  '141',
]);
export const USDA_ONLY_PDS_ACTION_CODES: ReadonlySet<GinnieMaePDSActionCode> = new Set(['036', '041', '078']);
export const VA_ONLY_PDS_ACTION_CODES: ReadonlySet<GinnieMaePDSActionCode> = new Set(['177']);

/**
 * ##### PDS Default Reason Codes (Appendix VI-22 §10)
 * Exactly one reason code per D record. 3 digits, left-zero-padded.
 */
export const GinnieMaePDSDefaultReasonCodes = z.enum([
  '001', // Death of Principal Borrower
  '002', // Illness of Principal Borrower
  '003', // Illness of Borrower's Family Member
  '004', // Death of Borrower's Family Member
  '005', // Marital Difficulties
  '006', // Curtailment of Income
  '007', // Excessive Obligations
  '008', // Abandonment of Property
  '009', // Distant Employment Transfer
  '011', // Property Problem
  '012', // Inability to Sell Property
  '013', // Inability to Rent Property
  '014', // Military Service
  '015', // Other
  '016', // Unemployment
  '017', // Business Failure
  '019', // Casualty Loss
  '022', // Energy-Environment Cost
  '023', // Servicing Problems
  '026', // Payment Adjustment
  '027', // Payment Dispute
  '029', // Transfer of Ownership Pending
  '030', // Fraud
  '031', // Unable to Contact Borrower
  '032', // National Emergency Declaration
  '034', // Eligible Disaster Area
  '035', // Incarceration
]);
export type GinnieMaePDSDefaultReasonCode = z.infer<typeof GinnieMaePDSDefaultReasonCodes>;

/**
 * ##### PDS Action Code / Date Pair
 * Each pair occupies 11 characters in the D record: 3-char code + 8-char YYYYMMDD date.
 * A D record must have at least 1 and at most 20 pairs.
 */
export const GinnieMaePDSActionCodeDatePair = z.object({
  actionCode: GinnieMaePDSActionCodes.describe('enum'),
  actionDate: zodDateOrString.describe('date'),
});
export type GinnieMaePDSActionCodeDatePair = z.infer<typeof GinnieMaePDSActionCodeDatePair>;

/**
 * ##### Ginnie Mae PDS Loan Record (V1)
 * Per-loan data stored in InvestorReporting. Maps to one D record in the output file.
 */
const GinnieMaePDSV1 = z.object({
  version: z.literal('1').describe('version'),

  /** Ginnie Mae Unique Loan ID — GNMA's system rejects rows with malformed values */
  gnmaLoanId: z.string().describe('string'),

  /** Ginnie Mae Pool ID — always 6 characters */
  poolId: z.string().length(6).describe('string'),

  /** Issuer's internal loan identifier — truncated to the 20-character field when the file is built */
  issuerLoanId: z.string().describe('string'),

  /** Default Reason Code — 3-digit code from the PDS reason code table */
  defaultReasonCode: GinnieMaePDSDefaultReasonCodes.describe('enum'),

  /**
   * Total months of current forbearance plan (including extensions).
   * Use 99 when not known or not applicable.
   */
  estimatedForbearanceTerm: z.number().describe('number'),

  /**
   * Action code / date pairs — 1 to 20 pairs.
   * Sorted chronologically ascending by date, then by numeric code.
   */
  actionCodeDatePairs: z.array(GinnieMaePDSActionCodeDatePair).min(1).max(20),
});
export type GinnieMaePDSV1 = z.infer<typeof GinnieMaePDSV1>;

export const GinnieMaePDS = GinnieMaePDSV1;
export type GinnieMaePDS = GinnieMaePDSV1;
